import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";

function KeysCard({ scope, title, subtitle, data, inputs, setInputs, save, clear, saving, loading, reveal, setReveal }) {
  const s = data[scope] || {};
  const inp = inputs[scope] || { key_id: "", key_secret: "" };
  const showVal = reveal[scope];
  return (
    <div className="bb-card p-6" data-testid={`razorpay-${scope}`}>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="bb-overline text-[var(--bb-muted)]">{scope.toUpperCase()}</div>
          <h3 className="font-serif text-xl text-[var(--bb-forest)] mt-1">{title}</h3>
          <p className="text-xs text-[var(--bb-muted)] mt-1">{subtitle}</p>
        </div>
        <span className={`bb-tag shrink-0 ${s.mode === "live" ? "bb-tag-green" : ""}`}>
          {loading ? "…" : (s.mode === "live" ? "LIVE" : "NOT CONFIGURED")}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--bb-muted)]">Loading…</div>
      ) : (
        <>
          <div className="text-xs text-[var(--bb-muted)] mb-4">
            {s.source === "db" ? "Custom keys (set via this panel)"
              : s.source === "env" ? "Environment variables"
              : "No keys configured — payments will fail until keys are added"}
            {s.has_keys && s.key_secret_masked && <span className="ml-2 font-mono">· secret: {s.key_secret_masked}</span>}
          </div>

          <div className="space-y-4">
            <div>
              <label className="bb-overline text-[var(--bb-muted)]">Key ID</label>
              <input
                type="text"
                placeholder="rzp_test_xxxxxxx or rzp_live_xxxxxxx"
                value={inp.key_id}
                onChange={(e) => setInputs((x) => ({ ...x, [scope]: { ...x[scope], key_id: e.target.value } }))}
                className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm font-mono"
                data-testid={`razorpay-${scope}-key-id`}
              />
            </div>
            <div>
              <label className="bb-overline text-[var(--bb-muted)] flex items-center justify-between">
                <span>Key Secret</span>
                <button
                  type="button"
                  onClick={() => setReveal((r) => ({ ...r, [scope]: !r[scope] }))}
                  className="text-[var(--bb-terracotta)] hover:underline normal-case tracking-normal"
                >
                  {showVal ? "Hide" : "Show"}
                </button>
              </label>
              <input
                type={showVal ? "text" : "password"}
                placeholder="Paste secret (leave empty to keep current)"
                value={inp.key_secret}
                onChange={(e) => setInputs((x) => ({ ...x, [scope]: { ...x[scope], key_secret: e.target.value } }))}
                className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm font-mono"
                data-testid={`razorpay-${scope}-key-secret`}
              />
              <div className="text-[11px] text-[var(--bb-muted)] mt-1">
                Secret is write-only — after save it's stored securely and only the last 4 chars are shown.
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => save(scope)}
              disabled={saving === scope || !inp.key_id || !inp.key_secret}
              className="bb-btn-primary text-sm disabled:opacity-60"
              data-testid={`razorpay-${scope}-save`}
            >
              {saving === scope ? "Saving…" : "Save keys"}
            </button>
            {s.source === "db" && (
              <button
                onClick={() => clear(scope)}
                className="text-sm text-[var(--bb-error)] hover:underline"
                data-testid={`razorpay-${scope}-clear`}
              >
                Clear & fall back
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminRazorpayKeys() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({
    donor: { key_id: "", key_secret: "" },
    audio: { key_id: "", key_secret: "" },
  });
  const [saving, setSaving] = useState(null);
  const [reveal, setReveal] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/razorpay-keys");
      setData(d);
      setInputs({
        donor: { key_id: d.donor?.key_id || "", key_secret: "" },
        audio: { key_id: d.audio?.key_id || "", key_secret: "" },
      });
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (scope) => {
    setSaving(scope);
    try {
      await api.put("/admin/razorpay-keys", {
        scope,
        key_id: inputs[scope].key_id.trim(),
        key_secret: inputs[scope].key_secret.trim(),
      });
      toast.success(`${scope === "donor" ? "Donor" : "Audio"} keys saved`);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(null); }
  };

  const clear = async (scope) => {
    if (!confirm(`Clear ${scope} Razorpay keys? Payments will fail until env vars or new keys are provided.`)) return;
    try {
      await api.put("/admin/razorpay-keys", { scope, key_id: "", key_secret: "" });
      toast.success("Cleared — falls back to environment variables");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const cardProps = { data, inputs, setInputs, save, clear, saving, loading, reveal, setReveal };

  return (
    <div className="space-y-5" data-testid="admin-razorpay-tab">
      <div className="bb-card p-6">
        <div className="bb-overline text-[var(--bb-muted)] mb-2">API KEYS · RAZORPAY</div>
        <h3 className="font-serif text-2xl text-[var(--bb-forest)]">Razorpay API Keys</h3>
        <p className="text-sm text-[var(--bb-muted)] mt-2 max-w-2xl">
          Paste live or test keys here to enable real payments. The app checks these values first, then falls back to environment variables. If neither is set, payments will fail with a clear error.
          Get your keys from <a className="bb-link" href="https://dashboard.razorpay.com/app/website-app-settings/api-keys" target="_blank" rel="noreferrer">Razorpay Dashboard → Settings → API Keys</a>.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <KeysCard scope="donor" title="Donor Platform" subtitle="Used for donations & pledges on balabasava.com" {...cardProps} />
        <KeysCard scope="audio" title="Audio Comics (OPC)" subtitle="Used for subscriptions on /audio-comics" {...cardProps} />
      </div>

      <div className="bb-card p-5 text-xs text-[var(--bb-muted)] leading-relaxed">
        <b className="text-[var(--bb-ink)]/80">Security note:</b> secrets are stored in MongoDB and never returned to the browser after save — only the last 4 characters are shown for verification. Clearing a key set makes the app fall back to environment variables (<code>RAZORPAY_KEY_ID/SECRET</code> for donor, <code>AC_RAZORPAY_KEY_ID/SECRET</code> for audio).
      </div>
    </div>
  );
}
